import { useEffect, useState } from "react";
import api from "../../lib/api";
import { SquarePen, MessageSquare, Trash2, Loader2 } from "lucide-react";

const ago = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short" });
};

export const HistoryList = ({ onNew, onOpen, footer }) => {
  const [list, setList] = useState(null);

  useEffect(() => {
    api.get("/ai/sessions").then(({ data }) => setList(data)).catch(() => setList([]));
  }, []);

  const remove = async (e, id) => {
    e.stopPropagation();
    setList((l) => l.filter((s) => s.session_id !== id));
    await api.delete(`/ai/sessions/${id}`).catch(() => {});
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col cai-fade-in" data-testid="cai-history">
      <div className="px-5 pb-2 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/80">Conversaciones</span>
        <button
          type="button"
          onClick={onNew}
          data-testid="cai-history-new"
          className="h-8 px-3 rounded-full bg-white/20 border border-white/30 text-[11px] font-semibold text-white hover:bg-white/30 transition-colors inline-flex items-center gap-1"
        >
          <SquarePen className="w-3.5 h-3.5" /> Nueva
        </button>
      </div>
      <div className="px-3 pb-3 space-y-2 overflow-y-auto cai-scroll">
        {list === null && (
          <div className="py-8 grid place-items-center text-white"><Loader2 className="w-5 h-5 animate-spin" /></div>
        )}
        {list?.length === 0 && (
          <div className="py-8 text-center text-white" data-testid="cai-history-empty">
            <MessageSquare className="w-6 h-6 mx-auto opacity-80" strokeWidth={1.5} />
            <div className="mt-2 text-[13px]">Sin conversaciones</div>
            <div className="text-[11.5px] text-white/75">Tus preguntas a la IA aparecerán aquí.</div>
          </div>
        )}
        {list?.map((s) => (
          <div
            key={s.session_id}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(s)}
            data-testid={`cai-history-${s.session_id}`}
            style={{ textShadow: "0 1px 2px rgba(30,58,95,0.25)" }}
            className="group w-full p-3 rounded-[18px] flex items-start gap-3 cursor-pointer cai-glass-hover border bg-[#1E3A5F]/12 border-white/15"
          >
            <span className="w-9 h-9 rounded-full bg-white/20 border border-white/30 grid place-items-center shrink-0 text-white">
              <MessageSquare className="w-4 h-4" strokeWidth={1.7} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold text-white truncate">{s.title}</span>
                <span className="text-[10px] text-white/70 shrink-0">{ago(s.updated_at)}</span>
              </span>
              <span className="block text-[12px] text-white/85 mt-0.5 truncate">
                {s.patient_name ? `Paciente: ${s.patient_name}` : "Consulta general"} · {Math.ceil(s.count / 2)} pregunta(s)
              </span>
            </span>
            <button
              type="button"
              onClick={(e) => remove(e, s.session_id)}
              title="Eliminar"
              data-testid={`cai-history-delete-${s.session_id}`}
              className="opacity-0 group-hover:opacity-100 w-7 h-7 grid place-items-center rounded-full text-white/85 hover:bg-white/20 transition-opacity shrink-0"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-auto pt-1">{footer}</div>
    </div>
  );
};
